// withAuth.tsx
// XSM-1536 - SSO Feature
// Author: Abhishek Bharti
// Updated on 20/11/2025
import { useState, useEffect, useCallback, useRef, ComponentType } from "react";
import { PRODUCT_ORIGIN, EVES_SUB_SYS_DOMAIN, BACKEND_BASE_URL, COMMON_SUB_DOMAIN } from "@/config";
import { concatAndEncodeBase64, getSubdomain } from "@/utils/common";
import subDomainClientMapping from "@/config/domainMapping";
import { useAuth } from "@/hooks/auth.hooks";

// Calculate thresholds based on environment
const refreshThreshold = 5 * 60 * 1000; // 5 minutes
const checkInterval = 30000; // 30 seconds

// Define the HOC type
const withAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  return function WithAuth(props: P) {
    const [isActive, setIsActive] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { handleLogout } = useAuth();

    const subDomain = getSubdomain();
    const { domain: userDomain, clientId, clientSecret } = subDomainClientMapping(subDomain);
    const defaultSubDomain =
      subDomain !== "" && subDomain !== COMMON_SUB_DOMAIN ? subDomain : "xsm";
    const accessTokenKey = `${defaultSubDomain}_access_token`;
    const refreshTokenKey = `${defaultSubDomain}_refresh_token`;
    const expiresInKey = `${defaultSubDomain}_expires_in`;

    const refreshTokenRef = useRef(
      localStorage.getItem(refreshTokenKey) || localStorage.getItem("xsm_refresh_token") || ""
    );
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Check for subdomain-specific token first, then fallback to generic token
    const isAuthenticated =
      localStorage.getItem(accessTokenKey) || localStorage.getItem("xsm_access_token");

    const refreshAccessToken = useCallback(async () => {
      if (isRefreshing) return;
      setIsRefreshing(true);

      //base64 authorization
      const basicAuthorozationSecret = concatAndEncodeBase64(
        clientId as string,
        clientSecret as string
      );

      try {
        const response = await fetch(`${BACKEND_BASE_URL}/xsm-api/v1/proxy/oauth2/token`, {
          method: "POST",
          headers: {
            Authorization: `Basic ${basicAuthorozationSecret}`,
            "Content-Type": "application/x-www-form-urlencoded",
            "Xsm-application": subDomain === (EVES_SUB_SYS_DOMAIN as string) ? "xsi" : "xsm",
            "Xsm-Domain": userDomain as string,
            "Xsm-iscompressed": "true",
            "X-Original-Origin": PRODUCT_ORIGIN
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            refresh_token: refreshTokenRef.current
          })
        });

        if (!response.ok) throw new Error("Failed to refresh access token");

        const data = await response.json();

        // For local testing, override expires_in with our test value
        const expiresInSeconds = data.expires_in;

        const expiresInMs = expiresInSeconds * 1000;
        const expirationTime = Date.now() + expiresInMs;

        // Store tokens with subdomain-specific keys
        localStorage.setItem(accessTokenKey, data.access_token);
        localStorage.setItem(refreshTokenKey, data.refresh_token);
        localStorage.setItem(expiresInKey, expirationTime.toString());
        localStorage.setItem(`${defaultSubDomain}_id_token`, data.id_token);
        // Also store with generic keys for backward compatibility
        localStorage.setItem("xsm_access_token", data.access_token);
        localStorage.setItem("xsm_refresh_token", data.refresh_token);
        localStorage.setItem("xsm_expires_in", expirationTime.toString());
        localStorage.setItem("xsm_id_token", data.id_token);
        refreshTokenRef.current = data.refresh_token;
      } catch (error) {
        handleLogout({ redirectToLogoutEndpoint: true });
      } finally {
        setIsRefreshing(false);
      }
    }, [isRefreshing, handleLogout]);

    const checkTokenStatus = useCallback(() => {
      const storedExpiry = Number(
        localStorage.getItem(expiresInKey) || localStorage.getItem("xsm_expires_in")
      );
      if (!isAuthenticated || !storedExpiry) {
        handleLogout({ redirectToLogoutEndpoint: false });
        return;
      }

      const currentTime = Date.now();
      const timeLeft = storedExpiry - currentTime;

      // Format time for better debugging
      const minutesLeft = Math.floor(timeLeft / 60000);
      const secondsLeft = Math.floor((timeLeft % 60000) / 1000);

      console.log(`Token status - ${minutesLeft}m ${secondsLeft}s remaining`, {
        expiresAt: new Date(storedExpiry).toLocaleTimeString(),
        currentTime: new Date(currentTime).toLocaleTimeString()
      });

      if (timeLeft <= 0) {
        console.log("Token expired - logging out");
        handleLogout({ redirectToLogoutEndpoint: true });
      } else if (timeLeft <= refreshThreshold && isActive) {
        console.log(`Token about to expire (${minutesLeft}m ${secondsLeft}s left) - refreshing...`);
        refreshAccessToken();
      }
    }, [isActive, isAuthenticated, refreshAccessToken, handleLogout, refreshThreshold]);

    useEffect(() => {
      if (isAuthenticated) {
        // Initial check
        checkTokenStatus();

        // Set up periodic checking
        intervalRef.current = setInterval(checkTokenStatus, checkInterval);
      }

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [isAuthenticated, checkTokenStatus, checkInterval]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        const isNowActive = document.visibilityState === "visible";
        setIsActive(isNowActive);
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }, []);

    if (!isAuthenticated) {
      return null; // or redirect to login
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuth;
